import {
	Alert,
	Button,
	Card,
	Group,
	Radio,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { type ReactNode, useEffect, useState } from "react";
import type {
	ProviderMode,
	ProviderModeStatusDto,
} from "@/types/ai-assistant-provider-mode";
import { saveProviderMode } from "./ai-assistant.api";
import {
	modeBlockedReason,
	PROVIDER_MODE_OPTIONS,
	rateWarningLines,
} from "./provider-mode.logic";

interface Props {
	status: ProviderModeStatusDto;
	disabled: boolean;
	cryptoConfigured: boolean;
	onSaved: (status: ProviderModeStatusDto) => void;
	/** Kontrol tambahan di bawah pilihan mode (mis. sakelar pindah otomatis). */
	children?: ReactNode;
}

/** Kartu "Mode penyedia": pilih profil Utama AI teks; berlaku untuk giliran chat berikutnya. */
export function ProviderModeCard({
	status,
	disabled,
	cryptoConfigured,
	onSaved,
	children,
}: Props) {
	const [selected, setSelected] = useState<ProviderMode>(status.mode);
	const [saving, setSaving] = useState(false);
	const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(
		null,
	);
	useEffect(() => setSelected(status.mode), [status.mode]);

	const blocked = modeBlockedReason(status, selected, cryptoConfigured);
	const dirty = selected !== status.mode;
	const warnings = rateWarningLines(status);

	const handleSave = async () => {
		setSaving(true);
		setMessage(null);
		try {
			onSaved(await saveProviderMode({ mode: selected }));
			setMessage({ ok: true, text: "Tersimpan — berlaku untuk giliran baru" });
		} catch (err) {
			setMessage({
				ok: false,
				text: err instanceof Error ? err.message : "Gagal menyimpan",
			});
		} finally {
			setSaving(false);
		}
	};

	return (
		<Card withBorder radius="md" p="lg">
			<Stack gap="sm">
				<Title order={5}>Mode penyedia</Title>
				<Text size="xs" c="dimmed">
					Memilih profil Utama untuk Chat dan Penunjuk. Kunci tidak dihapus saat
					mode diganti, dan sesi suara tidak terpengaruh.
				</Text>
				<Radio.Group
					value={selected}
					onChange={(v) => {
						setMessage(null);
						setSelected(v as ProviderMode);
					}}
				>
					<Stack gap="xs">
						{PROVIDER_MODE_OPTIONS.map((o) => (
							<Radio
								key={o.value}
								value={o.value}
								label={o.label}
								description={o.description}
								disabled={disabled}
							/>
						))}
					</Stack>
				</Radio.Group>
				{dirty && blocked && (
					<Text size="sm" c="red">
						{blocked}
					</Text>
				)}
				{warnings.map((w) => (
					<Alert
						key={w}
						color="yellow"
						icon={<IconAlertTriangle size={16} />}
						p="xs"
					>
						{w}
					</Alert>
				))}
				<Group justify="space-between">
					<Text size="sm" c={message?.ok ? "green" : "red"}>
						{message?.text}
					</Text>
					<Button
						onClick={handleSave}
						loading={saving}
						disabled={disabled || !dirty || blocked !== null}
					>
						Simpan mode
					</Button>
				</Group>
				{children}
			</Stack>
		</Card>
	);
}
